import { Container, type Sprite } from "pixi.js";
import { SpriteText, SpriteTextAlignment, SpriteTextFitMode } from "@slot/presentation";
import type { GridPoint, Log, PresentationTaskRunner, SpriteFontDefinition } from "@slot/presentation";
import type { CellMultiplierPort } from "./CellMultiplierPresenter";

/** Where a badge sits on its cell and how it lands (design units in the grid's space; seconds of the presentation runner). */
export interface CellMultiplierBadgeTuning {
  /** The badge's right edge and its digits' centre line, from the cell's centre (the cell's lower right, as the art shows). */
  offsetX: number;
  offsetY: number;
  /** The digits' box: the badge fits inside it whatever its length ("x2", "x128"). */
  boxWidth: number;
  boxHeight: number;
  /** The landing: from 0 past `popScale` and back to 1, on the runner (the speed modes scale it). */
  popSeconds: number;
  popScale: number;
  /**
   * The cell's tile under a badge: tinted by the badge's value, `colors[0]` for x2 and each next entry one step up (the
   * last for every higher value), at `alpha`; it fades to its colour as the badge lands and back to the plain tile as
   * the badge leaves, over `fadeSeconds` on the runner.
   */
  tile: { colors: readonly number[]; alpha: number; fadeSeconds: number };
}

/** The plain tile's tint: the tile art drawn as it is. */
const PLAIN_TINT = 0xffffff;

/**
 * The badges in the grid's space, over the symbols and inside the panel's clip: one sprite-text badge per badged cell,
 * in the multiplier font ("x" and digits), right-aligned at the cell's lower right. A badge lands with a pop; a value
 * change re-lands it. A badged cell's tile takes the badge's colour, warmer as the value grows, so the board shows its
 * hot cells at a glance. Without the font no badge shows (the asset load logged it); the tiles colour regardless.
 */
export class CellMultiplierView extends Container implements CellMultiplierPort {
  private readonly badges = new Map<number, SpriteText>();
  /** Bumped per badge by each landing; a stale pop stops writing. */
  private readonly generations = new Map<number, number>();
  /** Bumped per tile by each fade; a stale fade stops writing. */
  private readonly tileGenerations = new Map<number, number>();
  /** Each touched tile's resting alpha (the scene's), to fade back to. */
  private readonly plainAlphas = new Map<number, number>();
  /** The tiles in a badge's colour (or fading to it). */
  private readonly coloured = new Set<number>();

  public constructor(
    private readonly deps: {
      font: SpriteFontDefinition | null;
      /** A cell's centre in the grid's space (the grid layer's layout). */
      cellPosition: (positionIndex: number) => GridPoint;
      /** The tile under a cell (the scene's); null without the tile art. */
      tileAt: (positionIndex: number) => Sprite | null;
      runner: PresentationTaskRunner;
      tuning: CellMultiplierBadgeTuning;
      log: Log;
    },
  ) {
    super({ label: "CellMultiplierBadges" });
  }

  public async showBadge(positionIndex: number, multiplier: string): Promise<void> {
    const { colors, alpha } = this.deps.tuning.tile;
    const tint = colors[Math.min(Math.max(Number(multiplier) - 2, 0), colors.length - 1)]!;
    this.coloured.add(positionIndex);
    await Promise.all([this.landBadge(positionIndex, multiplier), this.fadeTile(positionIndex, tint, alpha)]);
  }

  public removeBadge(positionIndex: number): void {
    this.generations.set(positionIndex, (this.generations.get(positionIndex) ?? 0) + 1);
    const badge = this.badges.get(positionIndex);
    if (badge) badge.visible = false;
    // A tile is known (its resting alpha kept) once a badge first coloured it; without the tile art there is none.
    const plainAlpha = this.plainAlphas.get(positionIndex);
    if (this.coloured.delete(positionIndex) && plainAlpha !== undefined) void this.fadeTile(positionIndex, PLAIN_TINT, plainAlpha);
  }

  public clearBadges(): void {
    for (const index of new Set([...this.badges.keys(), ...this.coloured])) this.removeBadge(index);
  }

  private async landBadge(positionIndex: number, multiplier: string): Promise<void> {
    const badge = this.badgeAt(positionIndex);
    if (!badge) return;
    badge.setText(`x${multiplier}`);
    const generation = (this.generations.get(positionIndex) ?? 0) + 1;
    this.generations.set(positionIndex, generation);
    const { popSeconds, popScale } = this.deps.tuning;
    badge.scale.set(0, 0);
    await this.deps.runner.tweenProgress(
      popSeconds,
      (k) => {
        if (this.generations.get(positionIndex) !== generation || badge.destroyed) return;
        // Up to the peak over the first 60 %, back to rest over the rest.
        const scale = k < 0.6 ? (popScale * k) / 0.6 : popScale + (1 - popScale) * ((k - 0.6) / 0.4);
        badge.scale.set(scale, scale);
      },
      undefined,
      { easing: "linear" },
    );
  }

  /** The cell's tile from its current tint and alpha to `tint` at `alpha`, the channels mixed on the runner. */
  private async fadeTile(positionIndex: number, tint: number, alpha: number): Promise<void> {
    const tile = this.deps.tileAt(positionIndex);
    if (!tile || tile.destroyed) return;
    if (!this.plainAlphas.has(positionIndex)) this.plainAlphas.set(positionIndex, tile.alpha);
    const generation = (this.tileGenerations.get(positionIndex) ?? 0) + 1;
    this.tileGenerations.set(positionIndex, generation);
    const fromTint = tile.tint;
    const fromAlpha = tile.alpha;
    await this.deps.runner.tweenProgress(
      this.deps.tuning.tile.fadeSeconds,
      (k) => {
        if (this.tileGenerations.get(positionIndex) !== generation || tile.destroyed) return;
        tile.tint = mixColor(fromTint, tint, k);
        tile.alpha = fromAlpha + (alpha - fromAlpha) * k;
      },
      undefined,
      { easing: "quadOut" },
    );
  }

  /** The cell's badge, made the first time the cell is badged; shown. Null without the font. */
  private badgeAt(positionIndex: number): SpriteText | null {
    const { font, tuning } = this.deps;
    if (!font) return null;
    let badge = this.badges.get(positionIndex);
    if (!badge) {
      badge = new SpriteText({ font, maxCharacters: 5, alignment: SpriteTextAlignment.Right, fitMode: SpriteTextFitMode.Both, boxWidth: tuning.boxWidth, boxHeight: tuning.boxHeight, label: `Badge:${positionIndex}` });
      const cell = this.deps.cellPosition(positionIndex);
      badge.position.set(cell.x + tuning.offsetX, cell.y + tuning.offsetY);
      this.badges.set(positionIndex, badge);
      this.addChild(badge);
    }
    badge.visible = true;
    return badge;
  }
}

/** `from` → `to` by `k` (0..1), channel by channel. */
function mixColor(from: number, to: number, k: number): number {
  const channel = (shift: number): number => {
    const a = (from >> shift) & 0xff;
    const b = (to >> shift) & 0xff;
    return Math.round(a + (b - a) * k) << shift;
  };
  return channel(16) | channel(8) | channel(0);
}
